"use client";

import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import type { LifestylePrize } from "@/app/redux/features/api/lifestyle.types";
import { collectionAnchorId } from "../../hooks/useCollectionScroll";
import type { ResolvedCollection } from "../../hooks/useLifestyleCollections";
import PrizeCard from "../prizeCard/PrizeCard";
import CarouselArrows from "./CarouselArrows";

interface CollectionRowProps {
  collection: ResolvedCollection;
  onAddToCart: (prize: LifestylePrize) => void;
  /** The first row on the page loads its images eagerly. */
  isPriority?: boolean;
}

/**
 * One horizontal collection carousel, driven by Embla like the hero.
 *
 * `slidesToScroll: "auto"` keeps an arrow click moving a full viewport of cards
 * rather than one at a time, and `trimSnaps` stops the last page scrolling on
 * past the final card into empty space, which is what keeps the forward arrow
 * disabling exactly when there is nothing left to reach.
 */
export default function CollectionRow({ collection, onAddToCart, isPriority = false }: CollectionRowProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    slidesToScroll: "auto",
  });
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(false);

  useEffect(() => {
    if (!emblaApi) return;

    const syncArrows = () => {
      setCanScrollBack(emblaApi.canScrollPrev());
      setCanScrollForward(emblaApi.canScrollNext());
    };
    syncArrows();
    emblaApi.on("select", syncArrows).on("reInit", syncArrows);

    return () => {
      emblaApi.off("select", syncArrows).off("reInit", syncArrows);
    };
  }, [emblaApi]);

  const scrollBy = useCallback(
    (direction: -1 | 1) => {
      if (direction === -1) emblaApi?.scrollPrev();
      else emblaApi?.scrollNext();
    },
    [emblaApi]
  );

  return (
    <section
      id={collectionAnchorId(collection.id)}
      className="flex flex-col w-full pt-4 lg:pt-6 pb-2 lg:pb-4 gap-3 lg:gap-4">
      <div className="w-full px-4 lg:px-5">
        <div className="flex items-center justify-between w-full max-w-[1320px] mx-auto gap-3">
          <h2 className="text-lg font-semibold leading-6 uppercase text-dark-1 text-left !mb-0">
            {collection.displayName}
          </h2>
          <CarouselArrows
            label={collection.displayName}
            canScrollBack={canScrollBack}
            canScrollForward={canScrollForward}
            onScroll={scrollBy}
          />
        </div>
      </div>

      {/* Clips at the screen edges, which is what lets the desktop viewport
          inside stay unclipped without widening the page. */}
      <div className="w-full px-0 lg:px-5 overflow-hidden">
        {/* Desktop: the viewport *is* the content column, so a page of the
            carousel is exactly the four cards sitting under the heading, and
            leaving it unclipped keeps the cards either side of that page on
            show out to the screen edges. Mobile stays full-bleed and lets the
            cards travel the whole device width. */}
        <div ref={emblaRef} className="w-full lg:max-w-[1320px] lg:mx-auto overflow-hidden lg:overflow-visible">
          <div
            // Mobile insets by 4px, which puts the card's *text* on the heading
            // once the card's own 12px of padding is added. The inset rides on
            // the slides rather than the track because a `padding-right` here
            // is left out of the scroll width, which would park the final card
            // hard against the screen edge.
            className="flex items-stretch pl-1 lg:pl-0 gap-2 lg:gap-4 touch-pan-y select-none">
            {collection.prizes.map((prize, index) => (
              <div
                key={prize.ContentNodeId}
                // Four to a page on desktop: the track is the 1320px column, so
                // this resolves to the drawn 318px card and stays exact as the
                // column narrows.
                className={`shrink-0 w-[158px] lg:w-[calc((100%-48px)/4)] ${
                  index === collection.prizes.length - 1 ? "mr-1 lg:mr-0" : ""
                }`}>
                <PrizeCard prize={prize} onAddToCart={onAddToCart} isPriority={isPriority && index < 6} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
